import { theme } from '../../../theme';
import { StatusBadge } from './StatusBadge';
import { formatRelativeTime } from '../hooks/useRelativeTime';
import { RED_FLAGS, OBSERVABLE_SIGNS, SELF_SYMPTOMS, EMOTION_SYMPTOMS, THINKING_SYMPTOMS } from '../../sideline-assessment/data/decisionTree';
import type { IncidentRecord } from '../../incident-logging/types/incident.types';
import type { CaseStatus, OutcomeKey } from '../types/assessmentHistory.types';
import { OUTCOME_CONFIG } from '../types/assessmentHistory.types';

const SYMPTOM_LABELS = new Map<string, string>([
  ...RED_FLAGS.map((f): [string, string] => [f.id, f.label]),
  ...OBSERVABLE_SIGNS.map((s): [string, string] => [s.id, s.label]),
  ...SELF_SYMPTOMS.map((s): [string, string] => [s.id, s.label]),
  ...EMOTION_SYMPTOMS.map((s): [string, string] => [s.id, s.label]),
  ...THINKING_SYMPTOMS.map((s): [string, string] => [s.id, s.label]),
]);

const MAX_VISIBLE_SYMPTOMS = 3;
const MAX_SYMPTOM_LABEL_LENGTH = 30;

const getOutcomeBorderColor = (outcome: string | null): string => {
  const key = (outcome ?? 'no_signs') as OutcomeKey;
  switch (key) {
    case 'emergency': return 'border-l-sy-red';
    case 'possible_concussion': return 'border-l-amber-500';
    case 'monitoring': return 'border-l-yellow-500';
    case 'no_signs': return 'border-l-sy-green';
    default: return 'border-l-gray-400';
  }
};

const truncateLabel = (label: string): string =>
  label.length > MAX_SYMPTOM_LABEL_LENGTH
    ? label.slice(0, MAX_SYMPTOM_LABEL_LENGTH - 1) + '…'
    : label;

type AssessmentCardProps = {
  incident: IncidentRecord & { current_status: CaseStatus };
  onClick: () => void;
};

export const AssessmentCard = ({ incident, onClick }: AssessmentCardProps) => {
  const outcomeKey = (incident.outcome ?? 'no_signs') as OutcomeKey;
  const outcomeConfig = OUTCOME_CONFIG[outcomeKey] ?? OUTCOME_CONFIG.no_signs;

  const allSymptomIds = [
    ...(incident.assessment_data?.selected_red_flags ?? []),
    ...(incident.assessment_data?.selected_observable_signs ?? []),
    ...(incident.assessment_data?.selected_self_symptoms ?? []),
  ];
  const symptoms = allSymptomIds
    .map(id => SYMPTOM_LABELS.get(id))
    .filter(Boolean) as string[];

  const visibleSymptoms = symptoms.slice(0, MAX_VISIBLE_SYMPTOMS);
  const hiddenCount = symptoms.length - visibleSymptoms.length;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border-l-4 ${getOutcomeBorderColor(incident.outcome)} dark:bg-white/[0.03] bg-white dark:border-white/[0.06] border border-gray-200 dark:hover:bg-white/[0.06] hover:bg-gray-50 ${theme.anim.transition} cursor-pointer group`}
    >
      {/* Small mono ref */}
      <span className={`font-mono text-[11px] ${theme.text.muted} w-[90px] flex-shrink-0 hidden sm:block`}>
        {incident.incident_ref}
      </span>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={`text-sm font-semibold ${outcomeConfig.colorClass} ${theme.anim.colors} group-hover:opacity-90`}>
            {outcomeConfig.label}
          </span>
          {incident.subject_name && (
            <span className={`text-xs ${theme.text.muted} truncate`}>
              {incident.subject_name}
            </span>
          )}
        </div>

        {visibleSymptoms.length > 0 && (
          <p className={`text-[11px] ${theme.text.veryMuted} mt-0.5 truncate`}>
            {visibleSymptoms.map(truncateLabel).join(' · ')}
            {hiddenCount > 0 && ` +${hiddenCount} more`}
          </p>
        )}
      </div>

      {/* Status + time */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <StatusBadge status={incident.current_status} />
        <span className={`text-[11px] ${theme.text.muted} w-[50px] text-right hidden sm:block`}>
          {formatRelativeTime(incident.created_at)}
        </span>
      </div>
    </button>
  );
};
